import { createContext, useState, useEffect, useContext } from 'react';
import { hasPermission } from '../utils/permissions';

export const AuthContext = createContext(null);

// Export a named function that references the context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const AuthProvider = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [role, setRole] = useState('admin');
  const [admin, setAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = async () => {
    try {
      const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email');
      const adminId = sessionStorage.getItem('admin_id') || localStorage.getItem('admin_id');
      const isSuper = sessionStorage.getItem('is_super_admin') || localStorage.getItem('is_super_admin');
      const storedRole = sessionStorage.getItem('admin_role') || localStorage.getItem('admin_role');

      if (adminEmail && adminId) {
        setIsAdminAuthenticated(true);
        setIsSuperAdmin(isSuper === 'true' || isSuper === '1');
        setRole(storedRole || (isSuper === 'true' || isSuper === '1' ? 'super_admin' : 'admin'));
        setAdmin({
          email: adminEmail,
          id: adminId,
          full_name: localStorage.getItem('admin_full_name') || 'Admin'
        });
      } else {
        setIsAdminAuthenticated(false);
        setIsSuperAdmin(false);
        setRole('admin');
        setAdmin(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAdminAuthenticated(false);
      setIsSuperAdmin(false);
      setRole('admin');
      setAdmin(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (loginData) => {
    // Store admin credentials
    sessionStorage.setItem('admin_email', loginData.admin_email);
    sessionStorage.setItem('admin_id', loginData.admin_id);
    sessionStorage.setItem('is_super_admin', loginData.is_super_admin ? '1' : '0');

    localStorage.setItem('admin_email', loginData.admin_email);
    localStorage.setItem('admin_id', loginData.admin_id);
    localStorage.setItem('admin_full_name', loginData.full_name);
    localStorage.setItem('is_super_admin', loginData.is_super_admin ? '1' : '0');

    // Signed token — the unforgeable identity used for authorization on the
    // backend (works cross-domain where the session cookie is dropped). See T0-2.
    if (loginData.admin_token) {
      sessionStorage.setItem('admin_token', loginData.admin_token);
      localStorage.setItem('admin_token', loginData.admin_token);
    }

    // RBAC role (T2-1b). admin-login returns `role`; hirewithus-login returns
    // `admin_role` (its `role` field is the login persona). Fall back sensibly.
    const resolvedRole = loginData.admin_role || loginData.role
      || (loginData.is_super_admin ? 'super_admin' : 'admin');
    sessionStorage.setItem('admin_role', resolvedRole);
    localStorage.setItem('admin_role', resolvedRole);

    setIsAdminAuthenticated(true);
    setIsSuperAdmin(loginData.is_super_admin || false);
    setRole(resolvedRole);
    setAdmin({
      email: loginData.admin_email,
      id: loginData.admin_id,
      full_name: loginData.full_name
    });
  };

  const logout = async () => {
    // Enterprise-grade: nuke server session FIRST, then wipe ALL identity
    // keys (not just admin_*). Without the server call, the Flask session
    // cookie stays valid; a subsequent request re-uses the old identity.
    // Without wiping cross-role keys (employer_email, recruiter_email, ...),
    // stale localStorage from earlier sessions leaks into the next login.
    try {
      const { getApiUrl } = await import('../../config/api');
      // Try both admin-logout and universal /api/logout for defense in depth
      await Promise.allSettled([
        fetch(getApiUrl('/api/admin-logout'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
        fetch(getApiUrl('/api/logout'), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }),
      ]);
    } catch (_) {
      // Network failure should not block client-side cleanup.
    }

    const IDENTITY_STORAGE_KEYS = [
      // Employer / company owner
      'employer_email', 'employer_password', 'employer_token',
      'company_email', 'company_token', 'company_name', 'companyAccountId',
      // Super admin / platform admin
      'admin_email', 'admin_id', 'admin_full_name', 'admin_token',
      'admin_role', 'is_super_admin',
      // Third-party vendor recruiter
      'recruiter_email', 'recruiter_token',
      // Interview organizer
      'organizer_email', 'organizer_token',
      // Any other identity-shaped keys that may have accumulated
      'actor_email', 'user_email', 'user_token',
    ];
    for (const key of IDENTITY_STORAGE_KEYS) {
      try { localStorage.removeItem(key); } catch (_) {}
      try { sessionStorage.removeItem(key); } catch (_) {}
    }

    setIsAdminAuthenticated(false);
    setIsSuperAdmin(false);
    setRole('admin');
    setAdmin(null);
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  return (
    <AuthContext.Provider 
      value={{
        isAdminAuthenticated,
        isSuperAdmin,
        role,
        hasPerm: (permission) => isSuperAdmin || hasPermission(role, permission),
        admin,
        isLoading,
        login,
        logout,
        checkAuthStatus
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
